'use client'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import Cookies from 'js-cookie'


export const USER_STORE_KEY = 'user-store'

/**
 * 需求来源：HiWaifu 梦箱（dream.hiwaifu.com）免登录。
 * 生产环境把登录 cookie 写在父域（NEXT_PUBLIC_LOGIN_COOKIE_DOMAIN=hiwaifu.com），子域名的服务端在第一个请求里就能拿到
 * 登录态，不用跳转接力页；持久化一年（JWT 本身长期有效），退出登录时删除。
 * 只在生产配置：测试环境 web-api.hiwaifu.com 也在同一父域下，写父域会用测试账号覆盖线上的登录 cookie。
 * 未配置或当前域名不在该父域下时，照旧写当前域名的会话 cookie。
 */
const PARENT_DOMAIN = process.env.NEXT_PUBLIC_LOGIN_COOKIE_DOMAIN || ''
const LOGIN_COOKIES = ['userId', 'token', 'newToken', 'hw-device-id']

const parentCookie = () => {
  if (typeof window === 'undefined' || !PARENT_DOMAIN) return null
  const host = window.location.hostname
  return host === PARENT_DOMAIN || host.endsWith(`.${PARENT_DOMAIN}`)
    ? { domain: `.${PARENT_DOMAIN}`, expires: 365, secure: true, sameSite: 'lax' as const }
    : null
}

export const setLoginCookie = (name: string, value: string) => {
  const shared = parentCookie()
  if (!shared) return Cookies.set(name, value)
  // 旧版写的同名 cookie 只属于 www，先删掉，避免同名两份
  Cookies.remove(name)
  Cookies.set(name, value, shared)
}

const writeLoginCookies = ({ userId, token, newToken }) => {
  setLoginCookie('userId', String(userId))
  setLoginCookie('token', token)
  setLoginCookie('newToken', newToken)
  try {
    const deviceId = localStorage.getItem('hw-device-id')
    if (deviceId) setLoginCookie('hw-device-id', deviceId)
  } catch {
    // localStorage 不可用：子域名退回共享设备号
  }
}

const clearLoginCookies = () => {
  const shared = parentCookie()
  for (const name of LOGIN_COOKIES) {
    Cookies.remove(name)
    if (shared) Cookies.remove(name, { domain: shared.domain })
  }
}

const initializer = (set, get) => ({
  userId: null,
  token: null,
  newToken: null,
  isPending: true,// 数据是否从sotrage载入成功

  isLogined: false,
  login: ({
    userId,
    token,
    newToken }) => {
    set({ isLogined: true, userId, token, newToken })

    writeLoginCookies({ userId, token, newToken })
  },
  unLogin: () => {
    if (!get().isLogined) return
    set({
      isLogined: false,
      userId: null,
      token: null,
      newToken: null,
    })
    clearLoginCookies()
    // 动态加载：useUserStore 被全站引用，静态引入会把 firebase/auth 整包
    // 打进所有页面的首屏 bundle，显著加重水合长任务（INP 主因之一）
    import('@/vendor/firebase/authentication').then(m => m.firebaseSignOut())
  },
})

const createUserStore = () => persist(initializer, {
  name: USER_STORE_KEY,
  // ...
  onRehydrateStorage: (state) => {

    // optional
    return (state, error) => {
      if (error) {
        // console.log('an error happened during hydration', error)
      } else {
        if (state?.isLogined) {
          writeLoginCookies(state)
        } else if (Cookies.get('token')) {
          // 父域 cookie 现在是持久的：本地已是未登录（如清了站点数据）时一并清掉，免得子域名误以为仍登录
          clearLoginCookies()
        }
        state!.isPending = false
      }
    }
  },
},)

export const useUserStore = create(createUserStore())
